import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiCheck } from "react-icons/hi";
import { toast } from "react-toastify";
import useAuth from "../../../../hooks/useAuth";
import useRolePermissions from "../../../../utils/rolePermissions";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useGetEntityPreferencesByPath,
  useUpdateEntityPreferencesByPath,
} from "../../../../hooks/entity";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { Switch } from "../../../atoms/Switch";
import { PlansModal } from "../../../modals/PlansModal";

const BENEFIT_KEYS = ["benefit_1", "benefit_2", "benefit_3"] as const;

const CleanDatabaseToggle = () => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const rolePermissions = useRolePermissions(user);
  const permissions = usePermissions(user);
  const hasEnterpriseAccess =
    permissions.hasFeatureAccess("KEEP_UNVERIFIED_USERS_POLICY") &&
    !permissions.isPromoTrial();

  const { data: entityPreferences } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "keep_unverified_users"
  );

  const cleanDatabase = !entityPreferences?.keep_unverified_users;
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const { mutate, isLoading } = useUpdateEntityPreferencesByPath();

  if (!rolePermissions.isOwner) return null;

  const displayed = optimistic ?? cleanDatabase;

  const handleChange = (next: boolean) => {
    if (!hasEnterpriseAccess) {
      setShowPlansModal(true);
      return;
    }
    setOptimistic(next);
    mutate(
      {
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "keep_unverified_users",
        value: !next,
      },
      {
        onSuccess: () => {
          toast.success(t("dashboard.entities.entityPreferences.success"));
        },
        onSettled: () => setOptimistic(null),
      }
    );
  };

  return (
    <div className="w-full" id="cleanDatabase">
      <p className="mb-3 text-sm leading-relaxed text-gray-600">
        {t("dashboard.settings.cleanDatabase.description")}
      </p>

      <ul className="mb-4 space-y-1.5">
        {BENEFIT_KEYS.map((key) => (
          <li key={key} className="flex items-start gap-2">
            <HiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span className="text-sm leading-tight text-gray-700">
              {t(`dashboard.settings.cleanDatabase.${key}`)}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <label
          htmlFor="clean_database_enabled"
          className="text-sm font-medium text-gray-700"
        >
          {
            t(
              hasEnterpriseAccess
                ? displayed
                  ? "dashboard.settings.cleanDatabase.enabled"
                  : "dashboard.settings.cleanDatabase.disabled"
                : "dashboard.settings.cleanDatabase.lockedAction"
            ) as string
          }
        </label>
        {hasEnterpriseAccess ? (
          <Switch
            id="clean_database_enabled"
            checked={displayed}
            disabled={
              isLoading || !permissions.can(Cap.ENTITY_SETTINGS, Level.WRITE)
            }
            onChange={handleChange}
          />
        ) : (
          <UnlockBadge onClick={() => setShowPlansModal(true)} />
        )}
      </div>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
      />
    </div>
  );
};

export default CleanDatabaseToggle;
